import { useEffect, useState } from 'react';
import {
  TextField,
  InlineStack,
  Text,
  Box,
  Banner,
  Spinner,
  BlockStack,
  Badge,
} from '@shopify/polaris';
import { previewBoltPattern, type BoltPatternPreview } from '../api/bolt-patterns';
import { MatchSourceBadge } from './ConfidenceBadge';

interface Props {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  helpText?: string;
  /** If true, only a single canonical is allowed (vehicle). Multi allowed for wheels. */
  singleOnly?: boolean;
  /** Called with the latest preview whenever parse succeeds. */
  onPreview?: (patterns: BoltPatternPreview[]) => void;
}

/**
 * Raw bolt pattern input with a live preview table. Debounces by 400ms so
 * typing doesn't hammer the API. Used for both vehicle (singleOnly) and
 * wheel (multi) forms.
 */
export function BoltPatternEditor({
  value,
  onChange,
  label = 'Bolt pattern (raw)',
  helpText,
  singleOnly = false,
  onPreview,
}: Props) {
  const [patterns, setPatterns] = useState<BoltPatternPreview[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!value || !value.trim()) {
      setPatterns([]); setErrors([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const resp = await previewBoltPattern(value);
        if (cancelled) return;
        setPatterns(resp.patterns);
        setErrors(resp.errors);
        onPreview?.(resp.patterns);
      } catch (err) {
        if (cancelled) return;
        setPatterns([]);
        setErrors([(err as Error).message]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [value, onPreview]);

  const multiWarning = singleOnly && patterns.length > 1
    ? 'Vehicles support a single bolt pattern. Enter just one value or create separate vehicle rows per submodel.'
    : null;

  return (
    <BlockStack gap="300">
      <TextField
        label={label}
        value={value}
        onChange={onChange}
        autoComplete="off"
        placeholder={singleOnly ? '6x135' : '6x135 / 6x139.7'}
        helpText={helpText ?? (singleOnly
          ? 'One canonical. Canonical + match_source shown below.'
          : 'Multiple patterns allowed — separate with / , & or |')}
        multiline={singleOnly ? undefined : 2}
      />
      {loading && <InlineStack gap="200" blockAlign="center"><Spinner size="small" /><Text as="span" variant="bodySm" tone="subdued">Parsing…</Text></InlineStack>}
      {errors.length > 0 && (
        <Banner tone="critical" title="Could not parse bolt pattern">
          <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </Banner>
      )}
      {multiWarning && <Banner tone="warning">{multiWarning}</Banner>}
      {patterns.length > 0 && (
        <Box borderWidth="025" borderColor="border" borderRadius="200" padding="300" background="bg-surface-secondary">
          <BlockStack gap="200">
            <Text as="p" variant="bodySm" tone="subdued">
              Parsed {patterns.length} canonical{patterns.length === 1 ? '' : 's'}:
            </Text>
            {patterns.map((p, i) => (
              <InlineStack key={i} gap="300" blockAlign="center" wrap={false}>
                <Text as="span" variant="bodySm" tone="subdued">raw</Text>
                <Badge>{p.raw}</Badge>
                <Text as="span" variant="bodySm" tone="subdued">→ canonical</Text>
                <Badge tone="info">{p.canonical}</Badge>
                <MatchSourceBadge source={p.via} />
              </InlineStack>
            ))}
          </BlockStack>
        </Box>
      )}
    </BlockStack>
  );
}
